import { useMemo } from 'react'
import { KpiTile } from '../charts/KpiTile'
import { formatMoney } from '../format'
import { buildDrillRsql } from '../drill'
import { WidgetFrame } from './WidgetFrame'
import { useWidgetData } from './useWidgetData'
import { WIDGET_KEY, type WidgetProps } from './types'
import { runExport, type ExportColumn } from '../../../lib/export'
import type { OutcomeVarianceResult, VarianceCurrencyBucket } from '../../../types/insights'

const WIDGET_KEY_SELF = WIDGET_KEY.outcomeVariance

/** Reporting & Exports — one row per currency bucket, business-labelled, never raw keys. */
const OUTCOME_VARIANCE_EXPORT_COLUMNS: ExportColumn<VarianceCurrencyBucket>[] = [
  { id: 'currency', header: 'Currency', toText: (b) => b.currency },
  { id: 'variance', header: 'Variance (approved − credit result)', toText: (b) => formatMoney(b.sumVariance, b.currency) },
  { id: 'resolvedCount', header: 'Resolved filings', toText: (b) => String(b.resolvedCount) },
]

/**
 * AC3 — Claimed-vs-approved variance (data-model §14 #48). Hero tile 2 of 3.
 *
 * IB-162 follow-up: the endpoint's `data` is `{ buckets, pendingCount }`
 * (`OutcomeVarianceResult`), never the flat `{ totalClaimed, totalApproved, variancePct, ... }`
 * this widget originally assumed — consuming the wrong fields threw during render and (pre-error-
 * boundary) blanked the whole dashboard. The backend publishes only per-currency summed variance
 * (`sumVariance = Σ(approvedAmount − creditResult)`) plus resolved/pending counts, so this tile
 * shows exactly those real figures — no fabricated claimed/approved totals or variance percentage.
 * Defensive against a malformed/unexpected payload (degrade to the empty state, never throw).
 */
export function OutcomeVarianceWidget({ filter, skipRefetch, onDrill }: WidgetProps) {
  const { status, data, message, retry } = useWidgetData<OutcomeVarianceResult>(WIDGET_KEY_SELF, filter, skipRefetch)

  const buckets: VarianceCurrencyBucket[] = useMemo(
    () => (Array.isArray(data?.buckets) ? data.buckets : []),
    [data],
  )
  const pendingCount = typeof data?.pendingCount === 'number' ? data.pendingCount : 0
  const resolvedCount = buckets.reduce((sum, b) => sum + (b.resolvedCount ?? 0), 0)

  // The primary (largest by |variance|) currency bucket leads the KPI value; every currency's
  // variance also appears in the breakdown so a multi-currency portfolio is never silently reduced
  // to one figure.
  const primary = useMemo(() => {
    if (buckets.length === 0) return undefined
    return [...buckets].sort((a, b) => Math.abs(Number(b.sumVariance)) - Math.abs(Number(a.sumVariance)))[0]
  }, [buckets])

  const frameState =
    status === 'ok' && buckets.length === 0 && pendingCount === 0 ? 'empty' : status === 'loading' ? 'loading' : status

  return (
    <WidgetFrame
      title="Claimed vs. approved variance"
      icon="activity"
      state={frameState}
      message={message}
      onRetry={retry}
      drill={{ label: 'View filings', onClick: () => onDrill(buildDrillRsql(filter)) }}
      exportAction={
        buckets.length > 0
          ? { onClick: () => runExport('csv', 'Claimed vs approved variance', buckets, OUTCOME_VARIANCE_EXPORT_COLUMNS) }
          : undefined
      }
    >
      {status === 'ok' && (buckets.length > 0 || pendingCount > 0) ? (
        <KpiTile
          title="Claimed vs. approved variance"
          value={primary ? formatMoney(primary.sumVariance, primary.currency) : '—'}
          valueCaption={
            resolvedCount > 0
              ? `across ${resolvedCount} resolved filing${resolvedCount === 1 ? '' : 's'}`
              : 'no resolved filings yet'
          }
          delta={{
            label:
              pendingCount > 0
                ? `${pendingCount} filing${pendingCount === 1 ? '' : 's'} pending outcome`
                : 'All outcomes resolved',
            variant: pendingCount > 0 ? 'neutral' : 'favorable',
          }}
          breakdown={[
            ...buckets.map((b) => ({
              key: `variance-${b.currency}`,
              label: `Variance (${b.currency})`,
              value: formatMoney(b.sumVariance, b.currency),
            })),
            { key: 'resolved', label: 'Resolved outcomes', value: String(resolvedCount) },
            { key: 'pending', label: 'Pending outcomes', value: String(pendingCount) },
          ]}
        />
      ) : null}
    </WidgetFrame>
  )
}
